import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '../context/API';

export interface Category {
  id: string;
  category: string;
  [key: string]: any;
}

export interface Schedule {
  id: string;
  day_names: string[];
  start_time: string;
  end_time: string;
  [key: string]: any;
}

export interface Store {
  id: string;
  name: string;
  address?: string;
  streetName?: string;
  streetNumber?: string;
  apartment?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phoneNumber?: string;
  latitude?: number;
  longitude?: number;
  categories?: Category[];
  schedules?: Schedule[];
  operationalStatus?: string;
  statusMessage?: string | null;
  lastStatusUpdate?: string | null;
  merchant?: any;
  [key: string]: any;
}

export interface CreateStorePayload {
  name: string;
  address?: string;
  city?: string;
  phoneNumber?: string;
  streetName?: string;
  streetNumber?: string;
  apartment?: string;
  state?: string;
  zipCode?: string;
  categories: string[];
  schedules: string[];
  latitude?: number;
  longitude?: number;
}

export interface UpdateStorePayload extends CreateStorePayload {
  id: string;
}

interface StoresState {
  stores: Store[];
  categories: Category[];
  schedules: Schedule[];
  loading: {
    stores: boolean;
    categories: boolean;
    schedules: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  error: string | null;
}

const initialState: StoresState = {
  stores: [],
  categories: [],
  schedules: [],
  loading: {
    stores: false,
    categories: false,
    schedules: false,
    create: false,
    update: false,
    delete: false,
  },
  error: null,
};

// Fetch stores
export const fetchStores = createAsyncThunk<
  Store[],
  { merchantId?: string; role?: string },
  { rejectValue: string }
>(
  'stores/fetchStores',
  async ({ merchantId, role }, { rejectWithValue }) => {
    try {
      let res;
      if (role === 'admin' && merchantId) {
        res = await API.get(`/stores/by-merchant/${merchantId}`);
      } else {
        const token = localStorage.getItem('access_token');
        res = await API.get('/stores', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      return res.data.data || [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch stores'
      );
    }
  }
);

// Fetch categories
export const fetchCategories = createAsyncThunk<
  Category[],
  void,
  { rejectValue: string }
>('stores/fetchCategories', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get('/categories');
    return response.data.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch categories'
    );
  }
});

// Fetch schedules
export const fetchSchedules = createAsyncThunk<
  Schedule[],
  void,
  { rejectValue: string }
>('stores/fetchSchedules', async (_, { rejectWithValue }) => {
  try {
    const response = await API.get('/schedules');
    return response.data || [];
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || 'Failed to fetch schedules'
    );
  }
});

// Create store
export const createStore = createAsyncThunk<
  Store,
  CreateStorePayload,
  { rejectValue: string }
>('stores/createStore', async (payload, { rejectWithValue }) => {
  try {
    const response = await API.post('/stores', payload);
    if (response.data.success) {
      return response.data.data;
    }
    return rejectWithValue(response.data.message || 'Failed to create store');
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to create store'
    );
  }
});

// Update store
export const updateStore = createAsyncThunk<
  Store,
  UpdateStorePayload,
  { rejectValue: string }
>('stores/updateStore', async (payload, { rejectWithValue }) => {
  try {
    const { id, ...updateData } = payload;
    const response = await API.patch(`/stores/${id}`, updateData);
    if (response.data.success) {
      return response.data.data;
    }
    return rejectWithValue(response.data.message || 'Failed to update store');
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to update store'
    );
  }
});

// Delete store
export const deleteStore = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('stores/deleteStore', async (storeId, { rejectWithValue }) => {
  try {
    const response = await API.delete(`/stores/${storeId}`);
    if (response.data.success) {
      return storeId;
    }
    return rejectWithValue(response.data.message || 'Failed to delete store');
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || error.message || 'Failed to delete store'
    );
  }
});

const storesSlice = createSlice({
  name: 'stores',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearStores: (state) => {
      state.stores = [];
    },
  },
  extraReducers: (builder) => {
    // Fetch stores
    builder
      .addCase(fetchStores.pending, (state) => {
        state.loading.stores = true;
        state.error = null;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.loading.stores = false;
        state.stores = action.payload;
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.loading.stores = false;
        state.error = action.payload || 'Failed to fetch stores';
      });

    // Fetch categories
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading.categories = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading.categories = false;
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state) => {
        state.loading.categories = false;
      });

    // Fetch schedules
    builder
      .addCase(fetchSchedules.pending, (state) => {
        state.loading.schedules = true;
      })
      .addCase(fetchSchedules.fulfilled, (state, action) => {
        state.loading.schedules = false;
        state.schedules = action.payload;
      })
      .addCase(fetchSchedules.rejected, (state) => {
        state.loading.schedules = false;
      });

    // Create store
    builder
      .addCase(createStore.pending, (state) => {
        state.loading.create = true;
        state.error = null;
      })
      .addCase(createStore.fulfilled, (state, action) => {
        state.loading.create = false;
        state.stores.unshift(action.payload);
      })
      .addCase(createStore.rejected, (state, action) => {
        state.loading.create = false;
        state.error = action.payload || 'Failed to create store';
      });

    // Update store
    builder
      .addCase(updateStore.pending, (state) => {
        state.loading.update = true;
        state.error = null;
      })
      .addCase(updateStore.fulfilled, (state, action) => {
        state.loading.update = false;
        const index = state.stores.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.stores[index] = action.payload;
        }
      })
      .addCase(updateStore.rejected, (state, action) => {
        state.loading.update = false;
        state.error = action.payload || 'Failed to update store';
      });

    // Delete store
    builder
      .addCase(deleteStore.pending, (state) => {
        state.loading.delete = true;
        state.error = null;
      })
      .addCase(deleteStore.fulfilled, (state, action) => {
        state.loading.delete = false;
        state.stores = state.stores.filter((s) => s.id !== action.payload);
      })
      .addCase(deleteStore.rejected, (state, action) => {
        state.loading.delete = false;
        state.error = action.payload || 'Failed to delete store';
      });
  },
});

export const { clearError, clearStores } = storesSlice.actions;
export default storesSlice.reducer;
